import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, TicketPercent } from "lucide-react";
import { toast } from "sonner";

// Coupons manager: create, activate/deactivate, delete. Deactivating keeps the
// code (and its usage history) but customers can no longer apply it.
export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, one_per_customer: true });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/offers").then((r) => setOffers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) return toast.error("Code is required");
    setBusy(true);
    try {
      await api.post("/admin/offers", { ...form, code: form.code.trim().toUpperCase(), value: Number(form.value) || 0 });
      toast.success("Coupon created"); setOpen(false); setForm({ code: "", type: "percent", value: 10, one_per_customer: true }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const toggle = async (o) => {
    try {
      await api.patch(`/admin/offers/${o.id}`, { is_active: !o.is_active });
      toast.success(`${o.code} ${o.is_active ? "deactivated" : "activated"}`); load();
    } catch { toast.error("Update failed"); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete coupon ${o.code}?`)) return;
    try { await api.delete(`/admin/offers/${o.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-10">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">Promotions</div>
          <h2 className="font-display text-xl font-bold tracking-tight mt-1 inline-flex items-center gap-2"><TicketPercent className="w-5 h-5" aria-hidden /> Coupons</h2>
        </div>
        <Button data-testid="offers-add-btn" onClick={() => setOpen(true)} className="rounded-lg bg-[var(--qd-charcoal)] hover:bg-black"><Plus className="w-4 h-4 mr-1" aria-hidden /> New coupon</Button>
      </header>

      <div className="qd-admin-card overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Discount</th><th className="text-left p-3">Per customer</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={o.id} data-testid={`offer-row-${o.code}`} className={`border-t border-gray-100 ${o.is_active ? "" : "opacity-60"}`}>
                <td className="p-3 font-mono-alt font-semibold">{o.code}</td>
                <td className="p-3">{o.type === "percent" ? `${o.value}% off` : `₹${o.value} off`}</td>
                <td className="p-3 text-gray-600">{o.one_per_customer === false ? "Unlimited" : "Once"}</td>
                <td className="p-3">
                  <label className="inline-flex items-center gap-2">
                    <Switch data-testid={`offer-active-${o.code}`} checked={!!o.is_active} onCheckedChange={() => toggle(o)} />
                    <span className={`text-xs font-semibold ${o.is_active ? "text-green-700" : "text-gray-400"}`}>{o.is_active ? "Active" : "Inactive"}</span>
                  </label>
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`offer-delete-${o.code}`} onClick={() => del(o)} className="p-1.5 text-gray-500 hover:text-red-600" aria-label={`Delete ${o.code}`}><Trash2 className="w-4 h-4" aria-hidden /></button>
                </td>
              </tr>
            ))}
            {offers.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-gray-400">No coupons yet — create your first.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New coupon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="offer-code" placeholder="Code (e.g. WELCOME10)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
            <div className="flex gap-2">
              <select data-testid="offer-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm">
                <option value="percent">% off</option>
                <option value="flat">₹ flat off</option>
              </select>
              <Input data-testid="offer-value" type="number" min="0" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch data-testid="offer-one-per" checked={form.one_per_customer} onCheckedChange={(v) => setForm({ ...form, one_per_customer: v })} />
              One use per customer
            </label>
            <Button data-testid="offer-save" onClick={create} disabled={busy} className="w-full bg-[var(--qd-charcoal)] hover:bg-black">{busy ? "Creating…" : "Create coupon"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
